import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { ActionButton, AntdRadioButton, Filters, RadioOptions } from "./styles";
import { Space, Dropdown, Menu, Modal, Radio } from "antd";
import {
	CloseCircleOutlined,
	PlusOutlined,
	InfoCircleOutlined,
} from "@ant-design/icons";
import Main from "../../../../layout/main/Main";
import { COLORS, Typo } from "../../../../layout/theme";
import DataTable from "../../../../common/data-table/DataTable";
import SignInTemplate from "../../../../layout/sign-in-template/SignInTemplate";
import DropShadow from "../../../../common/drop-shadow/DropShadow";
import {
	get_active_ingredients_header_info,
	get_active_ingredients_page,
	update_active_ingredient_state,
} from "../../../../../api/endpoints/activeIngredients";
import debounce from "lodash/debounce";
import { BsThreeDotsVertical } from "react-icons/bs";
import LoadingHeader from "./components/LoadingHeader";
import AntdSearch from "../../../../common/antd-search/AntdSearch";
import PrimaryButton from "../../../../common/antd-buttons/PrimaryButton";

const { confirm } = Modal;

export default function ActiveIngredientsList() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");
	const [data, setData] = useState(null);
	const [state, setState] = useState("ACTIVE");
	const [headerInfo, setHeaderInfo] = useState(null);
	const [key, setKey] = useState("null");

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const columns = [
		{
			title: () => <div style={{ color: COLORS.White }}>Nombre</div>,
			dataIndex: "name",
			render: (value, record) => (
				<Link
					to={`/admin/activeIngredients/${key.id}?uri=${uri}`}
					onMouseOver={() => setKey(record)}
				>
					<Typo type="terciary" fontWeight={600} fontSize={14}>
						{value.toUpperCase()}
					</Typo>
				</Link>
			),
		},
		{
			title: "",
			key: "id",
			dataIndex: "id",
			width: 10,
			render: (f, r, i) => (
				<>
					<Dropdown
						overlay={!r.deleted ? menu : menuInactive}
						placement="bottomRight"
					>
						<ActionButton key={`${f}`} onMouseOver={() => setKey(f)}>
							<BsThreeDotsVertical />
						</ActionButton>
					</Dropdown>
				</>
			),
		},
	];

	const menu = (
		<Menu
			items={[
				{
					key: "1",
					label: (
						<Link to={`/admin/activeIngredients/${key}?uri=${uri}`}>
							<Typo type="secondary">Editar</Typo>
						</Link>
					),
				},
				{
					key: "2",
					label: <Typo onClick={() => handleDelete(key)}>Borrar</Typo>,
				},
			]}
		/>
	);

	const menuInactive = (
		<Menu
			items={[
				{
					key: "1",
					label: (
						<Link to={`/admin/activeIngredients/${key}?uri=${uri}`}>
							<Typo type="secondary">Editar</Typo>
						</Link>
					),
				},
				{
					key: "2",
					label: <Typo onClick={() => handleActivate(key)}>Habilitar</Typo>,
				},
			]}
		/>
	);

	const fetch_data_active_ingredients = async (page, search) => {
		const res = await get_active_ingredients_page(search, state, page);
		setData(res);
	};

	useEffect(() => {
		fetch_data_active_ingredients(1);
	}, [state]);

	const fetch_active_ingredients_header = async () => {
		const res = await get_active_ingredients_header_info();
		setHeaderInfo(res);
	};

	useEffect(() => {
		fetch_active_ingredients_header();
	}, [state]);

	useEffect(() => {
		return () => {
			debouncedEventHandler.cancel();
		};
	}, []);

	const eventHandler = (e) => {
		fetch_data_active_ingredients(1, e.target.value);
	};

	const debouncedEventHandler = useMemo(() => debounce(eventHandler, 250), []);

	const handleDelete = (key) => {
		confirm({
			title: "¿Seguro que quiere eliminar este Principio Activo?",
			icon: <CloseCircleOutlined style={{ color: "red" }} />,
			content: "El principio activo será eliminado al aceptar",
			okText: "Sí",
			okType: "danger",
			cancelText: "No",
			onOk: () => {
				deleteActiveIngredient(key);
			},
		});
	};

	const deleteActiveIngredient = async (id) => {
		const _state = { id: id, state: "DELETED" };
		await update_active_ingredient_state(id, _state);
		fetch_data_active_ingredients(1, "");
		fetch_active_ingredients_header();
	};

	const handleActivate = (key) => {
		confirm({
			title: "¿Seguro que quiere activar este Principio Activo?",
			icon: <InfoCircleOutlined style={{ color: "blue" }} />,
			content: "El Principio Activo será reactivado al aceptar",
			okText: "Sí",
			okType: "danger",
			cancelText: "No",
			onOk: () => {
				activateActiveIngredient(key);
			},
		});
	};

	const activateActiveIngredient = async (id) => {
		const _state = { id: id, state: "ACTIVATED" };
		await update_active_ingredient_state(id, _state);
		fetch_data_active_ingredients(1, "");
		fetch_active_ingredients_header();
	};

	return (
		<Main>
			<Main.Breadcrumb>
				<Main.BreadcrumbItem>
					{uri ? (
						<Link to={"/" + uri}>Configuración General</Link>
					) : (
						<Link to="/">Home</Link>
					)}
				</Main.BreadcrumbItem>
				<Main.BreadcrumbSeparator />
				<Main.ActiveBreadcrumbItem>
					Listado de Principios Activos
				</Main.ActiveBreadcrumbItem>
			</Main.Breadcrumb>

			<Main.Body>
				<Filters>
					{!headerInfo ? (
						<LoadingHeader />
					) : (
						<Main.FilterContainer>
							<AntdSearch
								width={400}
								allowClear
								height={35}
								placeholder="Buscar principio activo"
								onChange={debouncedEventHandler}
							/>
							<RadioOptions>
								<Radio.Group
									value={state}
									onChange={(e) => setState(e.target.value)}
								>
									<AntdRadioButton value="ACTIVE">
										Activo ({headerInfo?.totalActive})
									</AntdRadioButton>
									<AntdRadioButton value="DELETED">
										Inactivo ({headerInfo?.totalDisabled})
									</AntdRadioButton>
								</Radio.Group>
							</RadioOptions>
							<Main.FilterActions>
								<PrimaryButton
									icon={<PlusOutlined />}
									onClick={() =>
										navigate("/admin/activeIngredients/create?uri=" + uri)
									}
									style={{ width: "200px", fontWeight: "400" }}
								>
									Crear Principio Activo
								</PrimaryButton>
							</Main.FilterActions>
						</Main.FilterContainer>
					)}
				</Filters>

				<DataTable
					data={data}
					fetch={fetch_data_active_ingredients}
					columns={columns}
					containerMarginTop={"0px"}
				/>
			</Main.Body>
		</Main>
	);
}
